import React, {
  forwardRef,
  useContext,
  useEffect,
  useImperativeHandle,
  useState,
} from "react";
import { Form } from "react-bootstrap";
import Spinner from "react-bootstrap/Spinner";
import { GlobalContext } from "../../../context/GlobalContext";
import axios from "../../../api/axios";
import helpers from "../../../utils/helpers";
import { CustomAddressAutocomplete } from "../../shared/CustomAddressAutoComplete";

// Display labels are shortened to fit the paired-row layout; the value sent
// to the backend (`entityType`) stays the original full string unchanged.
const ENTITY_TYPE_OPTIONS = [
  { value: "Corporation", label: "Corporation" },
  { value: "LLC", label: "LLC" },
  { value: "Sole Proprietorship", label: "Sole prop" },
  { value: "Other", label: "Other" },
];

/**
 * PD-6 Milestone 2 — business path Step 2 "Business".
 *
 * Merges companyname.js, entitytype.js (stacked select -> chips),
 * bussinessAnual.js, longbussiness.js (select -> 6 chip bands),
 * address.js (company + home address) and DealSummary.js's
 * business_description field into one step.
 *
 * Every field keeps its own individual `partial-application` autosave POST
 * call with the exact same payload shape as its original standalone screen.
 */
const StepBusiness = forwardRef(({ goNext, goBack, isFirstStep, footNote }, ref) => {
  const { state, dispatch } = useContext(GlobalContext);
  const [showLoader, setShowLoader] = useState(false);
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [serverError, setServerError] = useState("");

  const [companyName, setCompanyName] = useState(
    state?.questions?.business?.companyName ?? ""
  );
  const [entityType, setEntityType] = useState(
    state?.questions?.business?.entityType ?? ""
  );
  const [annualRevenue, setAnnualRevenue] = useState(
    state?.questions?.business?.bussinessAnual
      ? parseInt(state?.questions?.business?.bussinessAnual)?.toLocaleString()
      : ""
  );
  const [longBussiness, setLongBussiness] = useState(
    state?.questions?.business?.longBussiness ?? ""
  );
  const [businessDescription, setBusinessDescription] = useState(
    state?.questions?.business?.businessDescription ?? ""
  );

  const [companyAddress, setCompanyAddress] = useState(
    state?.questions?.business?.companyAddress ?? ""
  );
  const [companyAddressComponents, setCompanyAddressComponents] = useState(
    JSON.parse(localStorage.getItem("companyComponentsMazo")) ?? []
  );
  const [companyAddressError, setCompanyAddressError] = useState("");
  const [companyUnitNumber, setCompanyUnitNumber] = useState(
    state?.questions?.business?.companyUnitNumber ?? ""
  );

  const [homeAddress, setHomeAddress] = useState(
    state?.questions?.business?.homeAddress ?? ""
  );
  const [homeAddressComponents, setHomeAddressComponents] = useState(
    JSON.parse(localStorage.getItem("homeComponentsMazo")) ?? []
  );
  const [homeAddressError, setHomeAddressError] = useState("");
  const [homeUnitNumber, setHomeUnitNumber] = useState(
    state?.questions?.business?.homeUnitNumber ?? ""
  );
  // Unit lines compared too — a ticked box locks the home unit line, so it
  // must never come back ticked over a different value it would then hide.
  const [sameAsCompanyAddress, setSameAsCompanyAddress] = useState(
    () =>
      Boolean(state?.questions?.business?.companyAddress) &&
      state?.questions?.business?.homeAddress ===
        state?.questions?.business?.companyAddress &&
      (state?.questions?.business?.homeUnitNumber ?? "") ===
        (state?.questions?.business?.companyUnitNumber ?? "")
  );

  // Selecting a suggestion fills in the address text immediately, but its
  // structured components (required for validation) only land once the
  // place-details lookup resolves. Track in-flight lookups so Continue can't
  // race ahead of them.
  const [pendingAddressLookups, setPendingAddressLookups] = useState(0);
  const handleAddressResolvingChange = (isResolving) => {
    setPendingAddressLookups((count) => count + (isResolving ? 1 : -1));
  };

  const [errors, setErrors] = useState({});

  // "Just saved now": every field on this step lands in global state as the
  // borrower types/selects, not only once they click Continue — so
  // navigating Back then Continue again restores whatever was filled in
  // here, instead of losing it because this component's local state was
  // discarded on unmount. (Address autocomplete *components*, needed for
  // validation, already survive navigation independently via localStorage —
  // this only needs to cover the plain search-string fields.)
  useEffect(() => {
    const parsedRevenue = parseInt(String(annualRevenue).replace(/,/g, ""));
    dispatch({
      type: "QUESTION_BUSINESS",
      payload: {
        ...state.questions.business,
        companyName,
        entityType,
        companyAddress,
        companyUnitNumber,
        homeAddress,
        homeUnitNumber,
        bussinessAnual: isNaN(parsedRevenue) ? null : parsedRevenue,
        longBussiness,
        businessDescription,
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    companyName,
    entityType,
    companyAddress,
    companyUnitNumber,
    homeAddress,
    homeUnitNumber,
    annualRevenue,
    longBussiness,
    businessDescription,
  ]);

  const handleAnnualRevenueChange = (e) => {
    const cleaned = e.target.value.replace(/,/g, "").replace(/[^0-9.]/g, "");
    const parsed = parseInt(cleaned);
    setAnnualRevenue(isNaN(parsed) ? "" : parsed.toLocaleString());
  };

  const handleSameAsCompanyAddressChange = (e) => {
    const checked = e.target.checked;
    setSameAsCompanyAddress(checked);
    if (checked) {
      setHomeAddress(companyAddress);
      setHomeAddressComponents(companyAddressComponents);
      setHomeAddressError("");
      setHomeUnitNumber(companyUnitNumber);
      // Mirror what a real selection does — persist the components so a
      // remount (e.g. Continue then Back) doesn't lose them.
      localStorage.setItem(
        "homeComponentsMazo",
        JSON.stringify(companyAddressComponents)
      );
    } else {
      setHomeAddress("");
      setHomeAddressComponents([]);
      setHomeUnitNumber("");
      localStorage.removeItem("homeComponentsMazo");
    }
  };

  const handleBusinessDescriptionChange = (e) => {
    setBusinessDescription(e.target.value);
  };

  // Backend field (business_duration) is a plain IntegerField, 1-100 — same
  // clamp the chips used to enforce, just typed instead of picked.
  const handleLongBussinessChange = (e) => {
    const digitsOnly = e.target.value.replace(/[^0-9]/g, "");
    if (digitsOnly === "") return setLongBussiness("");
    setLongBussiness(Math.min(parseInt(digitsOnly, 10), 100));
  };

  const validate = () => {
    const validationErrors = {};
    if (!companyName) validationErrors.companyName = "Please enter your company name.";
    if (!entityType) validationErrors.entityType = "Please select an entity type.";
    if (!annualRevenue) validationErrors.annualRevenue = "Please provide your annual revenue.";
    if (longBussiness === "" || longBussiness === null || longBussiness === undefined) {
      validationErrors.longBussiness = "Please enter how long you've been in business.";
    } else if (Number(longBussiness) < 1) {
      validationErrors.longBussiness = "Years in business must be at least 1.";
    }
    if (!companyAddress || companyAddressComponents.length === 0) {
      validationErrors.companyAddress = "Please select your company address from the list.";
    } else if (helpers.extractAddressComponents(companyAddressComponents) === false) {
      validationErrors.companyAddress =
        "Please enter a valid address with state, zip code, city and country.";
    }
    if (!homeAddress || homeAddressComponents.length === 0) {
      validationErrors.homeAddress = "Please select your home address from the list.";
    } else if (helpers.extractAddressComponents(homeAddressComponents) === false) {
      validationErrors.homeAddress =
        "Please enter a valid address with state, zip code, city and country.";
    }
    if (!businessDescription || businessDescription.trim().length < 3) {
      validationErrors.businessDescription = "Please tell us what the business does (at least 3 characters).";
    }
    return validationErrors;
  };

  // Exposed to StepShell.js's "Save & finish later" handler — see StepYou.js
  // for the full rationale. CustomAddressAutocomplete reads its error via a
  // dedicated prop rather than the generic `errors` object, so those two
  // fields need the same extra propagation handleContinue below already
  // does.
  useImperativeHandle(ref, () => ({
    validateForSaveLater: () => {
      setCompanyAddressError("");
      setHomeAddressError("");
      const validationErrors = validate();
      setErrors(validationErrors);
      setSubmitAttempted(true);
      if (validationErrors.companyAddress) setCompanyAddressError(validationErrors.companyAddress);
      if (validationErrors.homeAddress) setHomeAddressError(validationErrors.homeAddress);
      return Object.keys(validationErrors).length === 0;
    },
  }));

  // Re-run validation live once the borrower has tried to submit once, so a
  // field's error clears as soon as it becomes valid instead of waiting for
  // another Continue press.
  useEffect(() => {
    if (!submitAttempted) return;
    const validationErrors = validate();
    setErrors(validationErrors);
    setCompanyAddressError(validationErrors.companyAddress || "");
    setHomeAddressError(validationErrors.homeAddress || "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    submitAttempted,
    companyName,
    entityType,
    annualRevenue,
    longBussiness,
    companyAddress,
    companyAddressComponents,
    homeAddress,
    homeAddressComponents,
    businessDescription,
  ]);

  const handleContinue = async (e) => {
    e.preventDefault();
    if (pendingAddressLookups > 0) return;
    setSubmitAttempted(true);
    setServerError("");
    setCompanyAddressError("");
    setHomeAddressError("");

    const validationErrors = validate();
    setErrors(validationErrors);
    if (validationErrors.companyAddress) setCompanyAddressError(validationErrors.companyAddress);
    if (validationErrors.homeAddress) setHomeAddressError(validationErrors.homeAddress);
    if (Object.keys(validationErrors).length > 0) return;

    const parsedRevenue = parseInt(String(annualRevenue).replace(/,/g, ""));
    const company = helpers.extractAddressComponents(
      companyAddressComponents,
      { state: "short", route: "short" },
      companyUnitNumber
    );
    const home = helpers.extractAddressComponents(
      homeAddressComponents,
      { state: "short", route: "short" },
      homeUnitNumber
    );

    dispatch({
      type: "QUESTION_BUSINESS",
      payload: {
        ...state.questions.business,
        companyName,
        entityType,
        bussinessAnual: parsedRevenue,
        longBussiness,
        companyAddress,
        homeAddress,
        businessDescription,
      },
    });

    const endpoint = `lender/partial-application/${state?.informationSlug}/?uuid=${state?.applicationUUID}`;

    setShowLoader(true);
    try {
      // Each field keeps its own autosave call, matching the payload shape
      // of its original standalone screen. These are independent fields, so
      // fire them concurrently instead of one round trip at a time.
      // Preserves the existing double-`body`-wrapped POST from address.js
      // byte-for-byte: { body: { company_address, home_address } }.
      const body = {};
      if (company !== false) body.company_address = company;
      if (home !== false) body.home_address = home;

      const requests = [
        axios.post(endpoint, {
          company_name: companyName,
          equipment_use: state?.questions?.equipmentUse,
        }),
        axios.post(endpoint, { entity_type: entityType }),
        axios.post(endpoint, { annual_business_revenue: parsedRevenue }),
        axios.post(endpoint, { business_duration: parseInt(longBussiness) }),
        axios.post(endpoint, { body }),
        axios.post(endpoint, { business_description: businessDescription }),
      ];
      await Promise.all(requests);
      goNext();
    } catch (error) {
      setServerError(
        error?.response?.data?.message || "An error occurred. Please try again."
      );
    } finally {
      setShowLoader(false);
    }
  };

  return (
    <div className="pd6-step">
      <h2 className="pd6-step__title">Tell us about your business</h2>
      <p className="pd6-step__helper">
        Grouped so you can fill it in one pass rather than one question per screen.
      </p>

      <Form noValidate onSubmit={handleContinue} className="pd6-form">
        <div className="pd6-field-row">
          <Form.Group className="pd6-field" controlId="stepBusinessCompanyName">
            <Form.Label>
              Company name<span className="pd6-required">*</span>
            </Form.Label>
            <Form.Control
              type="text"
              autoComplete="off"
              placeholder="Enter your company name"
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
              isInvalid={submitAttempted && Boolean(errors.companyName)}
            />
            <Form.Control.Feedback type="invalid">{errors.companyName}</Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="pd6-field" controlId="stepBusinessEntityType">
            <Form.Label>
              Entity type<span className="pd6-required">*</span>
            </Form.Label>
            <div className="pd6-chip-group pd6-chip-group--nowrap">
              {ENTITY_TYPE_OPTIONS.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  className={`pd6-chip ${entityType === option.value ? "pd6-chip--selected" : ""}`}
                  onClick={() => setEntityType(option.value)}
                >
                  {option.label}
                </button>
              ))}
            </div>
            {submitAttempted && errors.entityType && (
              <div className="pd6-field-error">{errors.entityType}</div>
            )}
          </Form.Group>
        </div>

        <div className="pd6-field-row">
          <Form.Group className="pd6-field" controlId="stepBusinessAnnualRevenue">
            <Form.Label>
              Annual revenue<span className="pd6-required">*</span>
            </Form.Label>
            <div className="pd6-currency-input">
              <span className="pd6-currency-input__prefix">$</span>
              <Form.Control
                type="text"
                placeholder="0"
                autoComplete="off"
                value={annualRevenue}
                onChange={handleAnnualRevenueChange}
                isInvalid={submitAttempted && Boolean(errors.annualRevenue)}
              />
            </div>
            {submitAttempted && errors.annualRevenue && (
              <div className="pd6-field-error">{errors.annualRevenue}</div>
            )}
          </Form.Group>

          <Form.Group className="pd6-field" controlId="stepBusinessDuration">
            <Form.Label>
              Years in business<span className="pd6-required">*</span>
            </Form.Label>
            <Form.Control
              type="text"
              inputMode="numeric"
              placeholder="e.g. 22"
              autoComplete="off"
              value={longBussiness}
              onChange={handleLongBussinessChange}
              isInvalid={submitAttempted && Boolean(errors.longBussiness)}
            />
            {submitAttempted && errors.longBussiness && (
              <div className="pd6-field-error">{errors.longBussiness}</div>
            )}
          </Form.Group>
        </div>

        <Form.Group className="pd6-field" controlId="stepBusinessCompanyAddress">
          <CustomAddressAutocomplete
            label="Company address"
            addressSearchQuery={companyAddress}
            setAddressSearchQuery={(value) => {
              setCompanyAddress(value);
              if (sameAsCompanyAddress) setHomeAddress(value);
            }}
            setAddressComponents={(components) => {
              setCompanyAddressComponents(components);
              if (sameAsCompanyAddress) {
                setHomeAddressComponents(components);
                localStorage.setItem("homeComponentsMazo", JSON.stringify(components));
              }
            }}
            addressValidationError={submitAttempted ? companyAddressError : ""}
            setAddressValidationError={setCompanyAddressError}
            placeholder="company address"
            localStorageLabel="companyComponentsMazo"
            isRequired={true}
            onResolvingChange={handleAddressResolvingChange}
          />
          <Form.Control
            type="text"
            className="pd6-address-unit-input"
            placeholder="Apt, suite, or unit (optional)"
            autoComplete="off"
            value={companyUnitNumber}
            onChange={(e) => {
              setCompanyUnitNumber(e.target.value);
              if (sameAsCompanyAddress) setHomeUnitNumber(e.target.value);
            }}
          />
        </Form.Group>

        <Form.Group className="pd6-field" controlId="stepBusinessHomeAddress">
          <CustomAddressAutocomplete
            label="Home address"
            addressSearchQuery={homeAddress}
            setAddressSearchQuery={setHomeAddress}
            setAddressComponents={setHomeAddressComponents}
            addressValidationError={submitAttempted ? homeAddressError : ""}
            setAddressValidationError={setHomeAddressError}
            placeholder="home address"
            localStorageLabel="homeComponentsMazo"
            isRequired={true}
            onResolvingChange={handleAddressResolvingChange}
            // Locked while it mirrors the company address, same as its
            // unit line below — edits belong on the company address.
            disabled={sameAsCompanyAddress}
          />
          <Form.Control
            type="text"
            className="pd6-address-unit-input"
            placeholder="Apt, suite, or unit (optional)"
            autoComplete="off"
            value={homeUnitNumber}
            onChange={(e) => setHomeUnitNumber(e.target.value)}
            disabled={sameAsCompanyAddress}
          />
          <div className="pd6-checkbox-row">
            <input
              type="checkbox"
              id="stepBusinessSameAddress"
              checked={sameAsCompanyAddress}
              disabled={!companyAddress}
              onChange={handleSameAsCompanyAddressChange}
            />
            <label htmlFor="stepBusinessSameAddress">Same as company address</label>
          </div>
        </Form.Group>

        <Form.Group className="pd6-field" controlId="stepBusinessDescription">
          <Form.Label>
            What does the business do?<span className="pd6-required">*</span>
          </Form.Label>
          <Form.Control
            as="textarea"
            rows={3}
            placeholder="e.g. We install and service commercial HVAC systems for restaurants and small retail buildings."
            value={businessDescription}
            onChange={handleBusinessDescriptionChange}
            isInvalid={submitAttempted && Boolean(errors.businessDescription)}
          />
          <Form.Control.Feedback type="invalid">
            {errors.businessDescription}
          </Form.Control.Feedback>
          <div className="pd6-field-helper">
            <span>
              One or two sentences is plenty. This goes to the underwriter, not to a
              credit bureau.
            </span>
            {businessDescription.length > 250 && (
              <span
                className={`pd6-char-counter ${
                  businessDescription.length > 300 ? "pd6-char-counter--over" : ""
                }`}
              >
                {businessDescription.length}/300
              </span>
            )}
          </div>
        </Form.Group>

        {serverError && <div className="pd6-field-error mb-3">{serverError}</div>}

        {submitAttempted && Object.keys(errors).length > 0 && (
          <div className="pd6-form-summary-error">
            {Object.keys(errors).length} field{Object.keys(errors).length === 1 ? "" : "s"} still
            need{Object.keys(errors).length === 1 ? "s" : ""} an answer.
          </div>
        )}

        <div className="pd6-step__nav">
          <div className="pd6-step__nav-buttons">
            <button
              type="button"
              className="pd6-btn pd6-btn--ghost"
              onClick={goBack}
              disabled={showLoader || isFirstStep}
            >
              <span className="pd6-btn__arrow">←</span>
              <span>Back</span>
            </button>
            <button
              type="submit"
              className="pd6-btn pd6-btn--primary"
              disabled={showLoader || pendingAddressLookups > 0}
            >
              {showLoader ? (
                <Spinner style={{ width: "1.2rem", height: "1.2rem" }} />
              ) : (
                <>
                  <span>Continue</span>
                  <span className="pd6-btn__arrow">→</span>
                </>
              )}
            </button>
          </div>
          {footNote && <span className="pd6-step__foot-note">{footNote}</span>}
        </div>
      </Form>
    </div>
  );
});

export default StepBusiness;
